import {
  PRICE_SHORTCUTS,
  applyPriceShortcut,
  buildCollectionFilterUrl,
  changeCategory,
  clearFilterGroup,
  findMatchingPriceShortcut,
  isShoeSizeKey,
  normalizeFilterState,
  normalizePricePointInventory,
  parseAppliedFilterState,
  removeFilterValue,
} from "./cielohuk-direct-filter-core.js?v=20260720-1";
import { DIRECT_FILTER_CONFIG } from "./cielohuk-direct-filter-config.js?v=20260718-1";

const REMOVE_EVENT = "cielo-direct-filter:remove";
const GROUP_ORDER = [
  "category",
  "subcategory",
  "sleeve",
  "genre",
  "price",
  "color",
  "size",
];

let instanceCount = 0;

function cloneState(state) {
  return {
    ...state,
    colors: [...(state.colors ?? [])],
    sizes: [...(state.sizes ?? [])],
  };
}

function emptyState() {
  return {
    category: null,
    subcategory: null,
    sleeve: null,
    genre: null,
    colors: [],
    sizes: [],
    priceMin: null,
    priceMax: null,
  };
}

function createElement(tagName, className, text) {
  const element = document.createElement(tagName);
  if (className) element.className = className;
  if (text !== undefined) element.textContent = text;
  return element;
}

function parsePricePoints(element) {
  const source = element.querySelector("[data-cielo-price-points]");
  if (!source) return [];

  try {
    const parsed = JSON.parse(source.textContent || "[]");
    return normalizePricePointInventory(parsed);
  } catch {
    return [];
  }
}

function isStateEmpty(state) {
  return (
    !state.category &&
    !state.subcategory &&
    !state.sleeve &&
    !state.genre &&
    state.colors.length === 0 &&
    state.sizes.length === 0 &&
    state.priceMin === null &&
    state.priceMax === null
  );
}

function formatYen(value) {
  return new Intl.NumberFormat("ja-JP").format(value);
}

function priceLabel(state) {
  if (state.priceMin === null && state.priceMax === null) return null;
  if (state.priceMin === null) return `${formatYen(state.priceMax)}円以下`;
  if (state.priceMax === null) return `${formatYen(state.priceMin)}円以上`;
  return `${formatYen(state.priceMin)}〜${formatYen(state.priceMax)}円`;
}

class CielohukDirectFilter extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this._instanceId = ++instanceCount;
    this._baseCollectionPath = this.dataset.baseCollectionUrl;
    this._groups = this.querySelector("[data-cielo-filter-groups]");
    this._status = this.querySelector("[data-cielo-filter-status]");
    this._pricePoints = parsePricePoints(this);
    this._applied = parseAppliedFilterState({
      baseCollectionPath: this._baseCollectionPath,
      currentUrl: window.location.href,
      config: DIRECT_FILTER_CONFIG,
    });
    this._pending = cloneState(this._applied);

    this._onClick = this._handleClick.bind(this);
    this._onChange = this._handleChange.bind(this);
    this._onRemove = this._handleRemove.bind(this);
    this._onClose = this._discardPending.bind(this);
    this._dialog = this.closest("dialog");
    this.addEventListener("click", this._onClick);
    this.addEventListener("change", this._onChange);
    document.addEventListener(REMOVE_EVENT, this._onRemove);
    this._dialog?.addEventListener("close", this._onClose);
    this.render();
  }

  disconnectedCallback() {
    if (!this._connected) return;
    this.removeEventListener("click", this._onClick);
    this.removeEventListener("change", this._onChange);
    document.removeEventListener(REMOVE_EVENT, this._onRemove);
    this._dialog?.removeEventListener("close", this._onClose);
    this._dialog = null;
    this._connected = false;
  }

  _discardPending() {
    this._pending = cloneState(this._applied);
    this.render();
  }

  _showStatus(message, kind = "error") {
    if (!this._status) return;
    this._status.hidden = !message;
    this._status.dataset.kind = kind;
    this._status.textContent = message || "";
  }

  _selectedValues(group) {
    const value = this._pending[group.stateKey];
    return group.multiple ? value : value ? [value] : [];
  }

  _renderTagGroup(groupKey) {
    const group = DIRECT_FILTER_CONFIG.groups[groupKey];
    const selected = this._selectedValues(group);
    const details = createElement("details", "cielo-filter__group");
    details.open = true;
    details.dataset.group = groupKey;

    const summary = createElement("summary", "cielo-filter__summary");
    const title = createElement("span", "cielo-filter__title", group.title);
    summary.append(title);
    if (selected.length > 0) {
      summary.append(
        createElement("span", "cielo-filter__selected-count", String(selected.length)),
      );
    }
    summary.append(createElement("span", "cielo-filter__chevron", "⌄"));
    details.append(summary);

    const body = createElement("div", "cielo-filter__body");
    if (selected.length > 0) {
      const clear = createElement("button", "cielo-filter__group-clear", "クリア");
      clear.type = "button";
      clear.dataset.clearGroup = group.stateKey;
      body.append(clear);
    }

    const choices = createElement("div", "cielo-filter__choices");
    const allowedSubcategories =
      groupKey === "subcategory"
        ? DIRECT_FILTER_CONFIG.subcategoriesByCategory[this._pending.category] ?? []
        : null;

    for (const option of group.values) {
      if (allowedSubcategories && !allowedSubcategories.includes(option.key)) continue;
      if (
        groupKey === "size" &&
        isShoeSizeKey(option.key) &&
        this._pending.category !== "sho"
      ) {
        continue;
      }
      const label = createElement("label", "cielo-filter__choice");
      const isColorChoice = groupKey === "color";
      if (isColorChoice) label.classList.add("cielo-filter__choice--color");
      const input = document.createElement("input");
      input.type = group.multiple ? "checkbox" : "radio";
      input.name = `cielo-filter-${this._instanceId}-${groupKey}`;
      input.value = option.key;
      input.checked = selected.includes(option.key);
      input.dataset.filterGroup = groupKey;
      input.dataset.filterKey = option.key;
      label.append(input);
      const mark = createElement(
        "span",
        `cielo-filter__choice-mark${isColorChoice ? " cielo-filter__color-swatch" : ""}`,
      );
      mark.setAttribute("aria-hidden", "true");
      if (isColorChoice) {
        mark.style.setProperty("--cielo-filter-swatch", option.swatch);
      }
      label.append(mark);
      label.append(createElement("span", "cielo-filter__choice-label", option.label));
      choices.append(label);
    }
    body.append(choices);
    details.append(body);
    return details;
  }

  _renderPriceGroup() {
    const details = createElement("details", "cielo-filter__group cielo-filter__group--price");
    details.open = true;
    details.dataset.group = "price";
    const hasPrice = this._pending.priceMin !== null || this._pending.priceMax !== null;

    const summary = createElement("summary", "cielo-filter__summary");
    summary.append(createElement("span", "cielo-filter__title", "価格"));
    if (hasPrice) {
      summary.append(createElement("span", "cielo-filter__selected-count", "1"));
    }
    summary.append(createElement("span", "cielo-filter__chevron", "⌄"));
    details.append(summary);

    const body = createElement("div", "cielo-filter__body");
    if (hasPrice) {
      const clear = createElement("button", "cielo-filter__group-clear", "クリア");
      clear.type = "button";
      clear.dataset.clearGroup = "price";
      body.append(clear);
    }

    const inputs = createElement("div", "cielo-filter__price-inputs");
    for (const boundary of ["min", "max"]) {
      const label = createElement(
        "label",
        "cielo-filter__price-field",
      );
      label.append(
        createElement(
          "span",
          "cielo-filter__price-label",
          boundary === "min" ? "最低価格" : "最高価格",
        ),
      );
      const inputWrap = createElement("span", "cielo-filter__price-control");
      inputWrap.append(createElement("span", "cielo-filter__currency", "¥"));
      const input = document.createElement("input");
      input.type = "number";
      input.inputMode = "numeric";
      input.min = "0";
      input.step = "1";
      input.placeholder = boundary === "min" ? "下限なし" : "上限なし";
      input.value = this._pending[boundary === "min" ? "priceMin" : "priceMax"] ?? "";
      input.dataset.priceBoundary = boundary;
      input.disabled = this._pricePoints.length === 0;
      inputWrap.append(input);
      label.append(inputWrap);
      inputs.append(label);
    }
    body.append(inputs);

    let matchingShortcut = null;
    try {
      matchingShortcut = findMatchingPriceShortcut(this._pending);
    } catch {
      matchingShortcut = null;
    }
    const shortcuts = createElement("div", "cielo-filter__price-shortcuts");
    for (const shortcut of PRICE_SHORTCUTS) {
      const button = createElement("button", "cielo-filter__price-shortcut", shortcut.label);
      button.type = "button";
      button.dataset.priceShortcut = shortcut.id;
      button.disabled = this._pricePoints.length === 0;
      button.setAttribute("aria-pressed", String(matchingShortcut === shortcut.id));
      if (matchingShortcut === shortcut.id) button.classList.add("is-selected");
      shortcuts.append(button);
    }
    body.append(shortcuts);

    if (this._pricePoints.length === 0) {
      body.append(
        createElement(
          "p",
          "cielo-filter__notice",
          "価格フィルターはデータ同期後に利用できます。",
        ),
      );
    }
    details.append(body);
    return details;
  }

  render() {
    if (!this._groups) return;
    const fragment = document.createDocumentFragment();
    for (const groupKey of GROUP_ORDER) {
      if (groupKey === "subcategory" && !this._pending.category) continue;
      fragment.append(
        groupKey === "price"
          ? this._renderPriceGroup()
          : this._renderTagGroup(groupKey),
      );
    }
    this._groups.replaceChildren(fragment);
    const clearAll = this.querySelector("[data-cielo-clear-all]");
    if (clearAll) clearAll.disabled = isStateEmpty(this._pending);
  }

  _handleChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;

    if (input.dataset.priceBoundary) {
      const stateKey = input.dataset.priceBoundary === "min" ? "priceMin" : "priceMax";
      this._pending[stateKey] = input.value === "" ? null : Number(input.value);
      this._showStatus(null);
      this.render();
      return;
    }

    const groupKey = input.dataset.filterGroup;
    const optionKey = input.dataset.filterKey;
    const group = DIRECT_FILTER_CONFIG.groups[groupKey];
    if (!group || !optionKey) return;

    if (group.multiple) {
      const selected = new Set(this._pending[group.stateKey]);
      input.checked ? selected.add(optionKey) : selected.delete(optionKey);
      this._pending[group.stateKey] = group.values
        .filter((option) => selected.has(option.key))
        .map((option) => option.key);
    } else if (groupKey === "category") {
      this._pending = changeCategory(
        this._pending,
        optionKey,
        DIRECT_FILTER_CONFIG,
      );
    } else {
      this._pending[group.stateKey] = optionKey;
    }
    this._showStatus(null);
    this.render();
  }

  _clearGroup(stateKey) {
    this._pending = clearFilterGroup(
      this._pending,
      stateKey,
      DIRECT_FILTER_CONFIG,
    );
  }

  _handleClick(event) {
    const target = event.target.closest("button");
    if (!target || !this.contains(target)) return;

    // Kalles' drawer also observes bubbled button clicks. Keep every filter
    // action inside this component so price shortcuts and clear buttons do not
    // close the drawer and discard the pending selections before Apply.
    event.preventDefault();
    event.stopPropagation();

    if (target.dataset.priceShortcut) {
      this._pending = applyPriceShortcut(
        this._pending,
        target.dataset.priceShortcut,
      );
      this._showStatus(null);
      this.render();
      return;
    }
    if (target.dataset.clearGroup) {
      this._clearGroup(target.dataset.clearGroup);
      this._showStatus(null);
      this.render();
      return;
    }
    if (target.hasAttribute("data-cielo-clear-all")) {
      this._pending = emptyState();
      this._showStatus(null);
      this.render();
      return;
    }
    if (!target.hasAttribute("data-cielo-apply")) return;

    try {
      const result = buildCollectionFilterUrl({
        baseCollectionPath: this._baseCollectionPath,
        currentUrl: window.location.href,
        pending: this._pending,
        config: DIRECT_FILTER_CONFIG,
        pricePoints: this._pricePoints,
      });
      if (!result.ok) {
        this._showStatus("この価格帯の商品はありません。");
        return;
      }
      window.location.assign(result.url);
    } catch (error) {
      this._showStatus(
        error instanceof RangeError
          ? "最低価格は最高価格以下にしてください。"
          : "価格は0以上の整数で入力してください。",
      );
    }
  }

  _handleRemove(event) {
    const detail = event.detail;
    if (!detail || detail.baseCollectionPath !== this._baseCollectionPath) return;
    this._pending = removeFilterValue(
      this._applied,
      detail.stateKey,
      detail.key,
      DIRECT_FILTER_CONFIG,
    );

    try {
      const result = buildCollectionFilterUrl({
        baseCollectionPath: this._baseCollectionPath,
        currentUrl: window.location.href,
        pending: this._pending,
        config: DIRECT_FILTER_CONFIG,
        pricePoints: this._pricePoints,
      });
      if (!result.ok) {
        this._showStatus("この価格帯の商品はありません。");
        this.render();
        return;
      }
      window.location.assign(result.url);
    } catch (error) {
      this._showStatus(
        error instanceof RangeError
          ? "最低価格は最高価格以下にしてください。"
          : "価格は0以上の整数で入力してください。",
      );
      this.render();
    }
  }
}

class CielohukDirectFilterChips extends HTMLElement {
  connectedCallback() {
    if (this._connected) return;
    this._connected = true;
    this._onClick = this._handleClick.bind(this);
    this.addEventListener("click", this._onClick);
    this.render();
  }

  disconnectedCallback() {
    this.removeEventListener("click", this._onClick);
    this._connected = false;
  }

  _chip(label, stateKey, key = "") {
    const button = createElement("button", "cielo-filter-chips__chip");
    button.type = "button";
    button.dataset.stateKey = stateKey;
    button.dataset.key = key;
    button.setAttribute("aria-label", `${label}を解除する`);
    button.append(createElement("span", "", label));
    button.append(createElement("span", "cielo-filter-chips__remove", "×"));
    return button;
  }

  render() {
    const baseCollectionPath = this.dataset.baseCollectionUrl;
    const state = parseAppliedFilterState({
      baseCollectionPath,
      currentUrl: window.location.href,
      config: DIRECT_FILTER_CONFIG,
    });
    const chips = [];
    for (const groupKey of ["category", "subcategory", "sleeve", "genre", "color", "size"]) {
      const group = DIRECT_FILTER_CONFIG.groups[groupKey];
      const values = group.multiple
        ? state[group.stateKey]
        : state[group.stateKey]
          ? [state[group.stateKey]]
          : [];
      for (const key of values) {
        const label = group.values.find((item) => item.key === key)?.label;
        if (label) chips.push(this._chip(label, group.stateKey, key));
      }
    }
    const currentPriceLabel = priceLabel(state);
    if (currentPriceLabel) chips.push(this._chip(currentPriceLabel, "price"));

    if (chips.length === 0) {
      this.hidden = true;
      this.replaceChildren();
      return;
    }
    this.hidden = false;
    const wrapper = createElement("div", "cielo-filter-chips__inner");
    wrapper.append(createElement("span", "cielo-filter-chips__label", "選択中"));
    wrapper.append(...chips);
    this.replaceChildren(wrapper);
  }

  _handleClick(event) {
    const button = event.target.closest("[data-state-key]");
    if (!button || !this.contains(button)) return;
    event.preventDefault();
    event.stopPropagation();
    document.dispatchEvent(
      new CustomEvent(REMOVE_EVENT, {
        detail: {
          baseCollectionPath: this.dataset.baseCollectionUrl,
          stateKey: button.dataset.stateKey,
          key: button.dataset.key,
        },
      }),
    );
  }
}

if (!customElements.get("cielohuk-direct-filter")) {
  customElements.define("cielohuk-direct-filter", CielohukDirectFilter);
}
if (!customElements.get("cielohuk-direct-filter-chips")) {
  customElements.define("cielohuk-direct-filter-chips", CielohukDirectFilterChips);
}
